import {inject, injectable} from "inversify";
import type {IHttpClientService} from "data/services/http";
import type {AxiosResponse} from "axios";
import {Bindings} from "data/constants/bindings";
import type {
	IArticles,
	ICategories,
	ISections,
} from "data/stores/static_content/static_content.store";
import type {IPlayer} from "data/stores/players/players.store";
import type {ICountry} from "data/stores/countries/countries.store";
import type {IRound} from "data/stores/rounds/rounds.store";
import type {ISquad} from "data/stores/squads/squads.store";
import type {IChecksums} from "data/stores/checksum/checksum.store";
import {Language} from "data/enums";
import {IState} from "data/stores/states/states.store";
import type {ISportsbet} from "data/stores/sportsbet/sportsbet.store";
import type {IMonth} from "data/stores/months/months.store";
import {ITournamentStat} from "data/stores/tournament_stats/tournament_stats.store";
import {INotificationBar} from "data/stores/notification/notification.store";

export interface IJSONProvider {
	countries(): Promise<AxiosResponse<ICountry[]>>;
	states(): Promise<AxiosResponse<IState[]>>;
	rounds(): Promise<AxiosResponse<IRound[]>>;
	players(): Promise<AxiosResponse<IPlayer[]>>;
	squads(): Promise<AxiosResponse<ISquad[]>>;
	checksums(): Promise<AxiosResponse<IChecksums>>;
	months(): Promise<AxiosResponse<IMonth[]>>;

	translations(locale: string): Promise<AxiosResponse<Record<string, unknown>>>;
	helpCategories(locale: string): Promise<AxiosResponse<ICategories>>;
	helpSections(locale: string): Promise<AxiosResponse<ISections>>;
	helpArticles(locale: string): Promise<AxiosResponse<IArticles>>;
	sportsbet(): Promise<AxiosResponse<ISportsbet>>;
	tournamentStat(id: number): Promise<AxiosResponse<ITournamentStat>>;
	fetchNotif(): Promise<AxiosResponse<INotificationBar>>;
}

@injectable()
export class JSONProvider implements IJSONProvider {
	constructor(
		@inject(Bindings.JsonHTTPClient) private _jsonClient: IHttpClientService,
		@inject(Bindings.ContentJsonHTTPClient) private _contentJsonClient: IHttpClientService
	) {}

	countries = () => this._jsonClient.get<ICountry[]>("countries.json");
	states = () => this._jsonClient.get<IState[]>("states.json");
	rounds = () => this._jsonClient.get<IRound[]>("rounds.json");
	players = () => this._jsonClient.get<IPlayer[]>("players.json");
	squads = () => this._jsonClient.get<ISquad[]>("squads.json");
	checksums = () => this._jsonClient.get<IChecksums>("checksums.json");
	translations = (locale: string) =>
		this._jsonClient.get<Record<string, unknown>>(`loco/${locale}.json`);
	months = () => this._jsonClient.get<IMonth[]>("months.json");

	helpCategories = (locale: Language) =>
		this._contentJsonClient.get<ICategories>(`${locale}/categories.json`);
	helpSections = (locale: Language) =>
		this._contentJsonClient.get<ISections>(`${locale}/sections.json`);
	helpArticles = (locale: Language) =>
		this._contentJsonClient.get<IArticles>(`${locale}/articles.json?per_page=100`);
	sportsbet = () => this._jsonClient.get<ISportsbet>("sb_web.json");
	tournamentStat = (id: number) => this._jsonClient.get<ITournamentStat>(`form_guide/${id}.json`);
	fetchNotif = () => this._jsonClient.get<INotificationBar>(`notification_bar.json`);
}
